import { ref, computed } from 'vue'
import DashboardDataService from '@/services/DashboardDataService'
import CompanyDataService from '@/services/CompanyDataService'
import UserDataService from '@/services/UserDataService'
import OrderDataService from '@/services/OrderDataService'
import MailDataService from '@/services/MailDataService'
import type DashboardDataset from '@/types/DashboardDataset'
import type ResponseData from '@/types/ResponseData'

export type Entity = 'companies' | 'users' | 'orders' | 'mails'

const ENTITY_LABELS: Record<Entity, string> = {
  companies: 'Spoločnosti',
  users: 'Klienti',
  orders: 'Objednávky',
  mails: 'Zásielky',
}

interface YearSeries {
  year: number
  series: Record<Entity, number>
}

export interface EntityYearSeries {
  year: number
  values: number[]
}

const emptyYearSeries = (): Record<Entity, number> => ({
  companies: 0, users: 0, orders: 0, mails: 0,
})

export function useDashboardData() {
  const loading = ref(false)
  const error = ref<Error | null>(null)

  const totals = ref({ companies: 0, users: 0, orders: 0, mails: 0 })
  const monthly = ref<Record<Entity, number[]>>({
    companies: Array(12).fill(0),
    users: Array(12).fill(0),
    orders: Array(12).fill(0),
    mails: Array(12).fill(0),
  })
  const byYear = ref<YearSeries[]>([])

  // raw backend arrays
  const raw = ref<Record<Entity, DashboardDataset[]>>({
    companies: [],
    users: [],
    orders: [],
    mails: [],
  })

  // per-entity rows kept as dense Jan–Dec arrays for the dashboard charts
  const entityYears = computed<Record<Entity, EntityYearSeries[]>>(() => {
    const result = {} as Record<Entity, EntityYearSeries[]>
    for (const entity of ['companies', 'users', 'orders', 'mails'] as Entity[]) {
      result[entity] = raw.value[entity]
        .map((row) => {
          const values = Array(12).fill(0) as number[]
          const months = row.labels.split(',')
          const data = row.data.split(',')
          months.forEach((month, i) => {
            const index = Number(month) - 1
            if (index >= 0 && index < 12) values[index] = Number(data[i] ?? 0)
          })
          return { year: Number(row.label), values }
        })
        .filter((row) => !Number.isNaN(row.year))
        .sort((a, b) => a.year - b.year)
    }
    return result
  })

  function transform() {
    const parsed = entityYears.value
    const entities = ['companies', 'users', 'orders', 'mails'] as Entity[]

    // byYear: union of all years across the four arrays
    const yearSet = new Set<number>()
    for (const entity of entities) {
      for (const row of parsed[entity]) yearSet.add(row.year)
    }
    const years = Array.from(yearSet).sort((a, b) => a - b)

    byYear.value = years.map((year) => {
      const series = emptyYearSeries()
      for (const entity of entities) {
        const row = parsed[entity].find((r) => r.year === year)
        series[entity] = row ? row.values.reduce((a, b) => a + b, 0) : 0
      }
      return { year, series }
    })

    // monthly: dense 12-slot array for the current year
    const currentYear = new Date().getFullYear()
    const newMonthly = {} as Record<Entity, number[]>
    for (const entity of entities) {
      const row = parsed[entity].find((r) => r.year === currentYear)
      newMonthly[entity] = row ? [...row.values] : Array(12).fill(0)
    }
    monthly.value = newMonthly
  }

  async function load() {
    loading.value = true
    error.value = null
    try {
      const dashResp = (await DashboardDataService.getAllDashboardData()) as ResponseData
      const d = dashResp?.data ?? {}
      raw.value = {
        companies: d.companies ?? [],
        users: d.users ?? [],
        orders: d.orders ?? [],
        mails: d.mails ?? [],
      }
      transform()

      // totals from list endpoints' pagination metadata
      const [c, u, o, m] = await Promise.all([
        CompanyDataService.getAllCompanies(1),
        UserDataService.getAllUsers(1),
        OrderDataService.getAllOrders(1, 1),
        MailDataService.getAllMails(1),
      ])
      totals.value = {
        companies: (c as ResponseData)?.data?.data?.total ?? (c as ResponseData)?.data?.total ?? 0,
        users: (u as ResponseData)?.data?.data?.total ?? (u as ResponseData)?.data?.total ?? 0,
        orders: (o as ResponseData)?.data?.data?.total ?? (o as ResponseData)?.data?.total ?? 0,
        mails: (m as ResponseData)?.data?.data?.total ?? (m as ResponseData)?.data?.total ?? 0,
      }
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
    } finally {
      loading.value = false
    }
  }

  function retry() {
    load()
  }

  // initial load
  load()

  return {
    totals,
    monthly,
    byYear,
    entityYears,
    loading,
    error,
    retry,
    entityLabels: ENTITY_LABELS,
  }
}
